import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { MessageSquare, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminInbox — full-system view of every classified/job conversation
 * with a drill-down panel for fraud monitoring.
 */
export default function AdminInbox() {
  const { authFetch } = useAdmin();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);

  async function load() {
    setLoading(true);
    const r = await authFetch(`${API}/admin/messages/conversations`);
    if (r.ok) { const d = await r.json(); setConversations(d.conversations || []); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function openConv(c) {
    setActive(c);
    const r = await authFetch(`${API}/admin/messages/conversation/${c.conversation_id}`);
    if (r.ok) { const d = await r.json(); setMessages(d.messages || []); }
  }

  async function delMsg(id) {
    if (!window.confirm('Obrisati ovu poruku? (Audit zapisi neće biti vraćeni.)')) return;
    const r = await authFetch(`${API}/admin/messages/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Obrisano'); openConv(active); } else toast.error('Greška');
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-inbox">
      <div className="flex items-center gap-3 mb-5">
        <MessageSquare className="text-blue-400" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Inbox — sve konverzacije</h1>
          <p className="text-sm text-zinc-400">Praćenje user-to-user komunikacije za prevenciju prijevare. Pretraga + brisanje pojedine poruke.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[360px,1fr] gap-4">
        <div className="rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950 max-h-[70vh] overflow-y-auto" data-testid="conv-list">
          {loading ? <div className="p-4"><Loader2 className="animate-spin text-zinc-500" /></div> : (
            conversations.length === 0
              ? <p className="text-zinc-500 text-sm p-5">Nema konverzacija.</p>
              : conversations.map(c => (
                <button key={c.conversation_id} onClick={() => openConv(c)}
                        className={`block w-full text-left px-3 py-2.5 border-b border-zinc-800 hover:bg-zinc-900 ${active?.conversation_id === c.conversation_id ? 'bg-zinc-900' : ''}`}
                        data-testid={`conv-${c.conversation_id}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs uppercase text-zinc-500">{c.listing_type === 'classified' ? 'Oglas' : 'Posao'} · {c.count} poruke</span>
                    <span className="text-[10px] text-zinc-600">{(c.last_at || '').slice(0, 10)}</span>
                  </div>
                  <div className="text-sm font-semibold text-zinc-200 truncate">{c.listing_title || '—'}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{c.sender_email} → {c.recipient_email}</div>
                  <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{c.last_message}</div>
                </button>
              ))
          )}
        </div>

        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 min-h-[60vh]" data-testid="conv-detail">
          {!active ? (
            <p className="text-zinc-500 text-sm flex items-center gap-2 justify-center mt-12"><AlertTriangle size={14}/> Odaberite konverzaciju s lijeve strane.</p>
          ) : (
            <>
              <div className="mb-3 pb-3 border-b border-zinc-800">
                <p className="text-xs uppercase text-zinc-500 mb-1">{active.listing_type === 'classified' ? 'Oglas' : 'Posao'}</p>
                <p className="font-semibold">{active.listing_title}</p>
                <p className="text-xs text-zinc-500">{active.sender_email} ↔ {active.recipient_email}</p>
              </div>
              {messages.map(m => (
                <div key={m.id} className="mb-3 p-3 rounded bg-zinc-900 border border-zinc-800" data-testid={`msg-${m.id}`}>
                  <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1">
                    <span><strong className="text-zinc-300">{m.sender_email}</strong> → {m.recipient_email}</span>
                    <span>{(m.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap text-zinc-200">{m.body}</p>
                  <div className="text-right mt-1">
                    <button onClick={() => delMsg(m.id)} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`msg-del-${m.id}`}>
                      <Trash2 size={11} /> Obriši
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
